/**
 * Effective Access Panel (ORCH-337, ORCH-21 §10.5, §11)
 *
 * Purpose: The Role Manager's preview: what a member can actually open and do in
 *          the active tenant, and on which records. It shows their roles, the
 *          areas they open, each scoped permission's coverage per dimension (one
 *          line per grant, since roles add together but never blend), and their
 *          other permissions. Everything comes from GET /authz/explain, so it is
 *          the same decision the server makes, not one rebuilt here.
 * Access: everyone in the Role Manager sees their own access. Picking another
 *         member needs user.view and audit.view (the server enforces the same).
 *         A value none of the viewer's own grants reach is counted but not
 *         named.
 * Layout: the scoped table and the other-permissions chips are grouped by Area
 *         (ORCH-415), and the scoped table's groups start collapsed.
 * Dependencies: authzService (explain, member search)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAuth } from '../../../context/AuthContext';
import { PermissionKey } from '../../../constants/permissionKeys';
import { authzService, roleErrorMessage } from '../../../services/authzService';
import {
  Dimension,
  ExplainableUser,
  ExplainCoverage,
  ExplainGrant,
  ExplainPermission,
  Explanation,
} from '../../../types/authz';

const MODULE_PREFIX = 'module.';
const LEVEL_LABEL: Record<string, string> = { read: 'Read-only', edit: 'Editable' };
const SOURCE_LABEL: Record<ExplainGrant['source'], string> = {
  role: 'Role',
  baseline: 'Everyone',
  module: 'From an area',
};

interface EffectiveAccessPanelProps {
  dimensions: Dimension[];
  /** Catalog labels by key, for areas and permissions. */
  labels: Record<string, string>;
  /** The Area label each permission is grouped under (ORCH-415); missing means "Other". */
  areaLabels?: Record<string, string>;
  /** Open on this member's access (the User Manager's Effective Access action). */
  initialMember?: ExplainableUser | null;
}

export const memberName = (user: ExplainableUser): string => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name ? `${name} (${user.email})` : user.email;
};

/**
 * One grant's coverage on one dimension, in words: "ALL", or its top values.
 *
 * An unnamed value is outside the viewer's own grants when explaining
 * someone else; in your own access every value is named, so an unnamed one is a
 * value that no longer exists.
 */
export const describeCoverage = (
  coverage: ExplainCoverage | undefined,
  ownAccess = false
): string => {
  if (!coverage) return '–';
  if (coverage.all) return 'ALL';
  const values = coverage.values ?? [];
  if (values.length === 0) return 'Nothing';
  const named = values.filter((v) => v.label).map((v) => v.label as string);
  const hidden = values.length - named.length;
  const parts = [...named];
  if (hidden > 0)
    parts.push(ownAccess ? `${hidden} unknown value(s)` : `${hidden} outside your scope`);
  return parts.join(', ');
};

/** Words for a failed explain: a picked member who isn't active here is a 404. */
export const explainErrorMessage = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) return 'That person isn’t an active member of this tenant.';
  return roleErrorMessage(error, 'Failed to load access');
};

const OTHER_AREA_LABEL = 'Other';

/** Permissions grouped by Area label, in first-seen order. */
export const groupByArea = <T extends { permission: string }>(
  permissions: T[],
  areaLabels: Record<string, string>
): [string, T[]][] => {
  const groups = new Map<string, T[]>();
  permissions.forEach((p) => {
    const area = areaLabels[p.permission] ?? OTHER_AREA_LABEL;
    groups.set(area, [...(groups.get(area) ?? []), p]);
  });
  return Array.from(groups.entries());
};

const grantSource = (grant: ExplainGrant): string =>
  grant.source === 'role' ? (grant.role_name ?? 'Role') : SOURCE_LABEL[grant.source];

const EffectiveAccessPanel: React.FC<EffectiveAccessPanelProps> = ({
  dimensions,
  labels,
  areaLabels = {},
  initialMember = null,
}) => {
  const [openAreas, setOpenAreas] = useState<Record<string, boolean>>({});
  const { can, state } = useAuth();
  const canExplainOthers = can(PermissionKey.USER_VIEW) && can(PermissionKey.AUDIT_VIEW);

  // Only someone who may explain others starts on another member's access
  const [member, setMember] = useState<ExplainableUser | null>(
    canExplainOthers ? initialMember : null
  );
  const [search, setSearch] = useState(member ? memberName(member) : '');
  const [options, setOptions] = useState<ExplainableUser[]>([]);
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    // Never leave the previous person's access on screen under a new name
    setExplanation(null);
    authzService
      .explain(member?.user_id, member?.tenant_id)
      .then((data) => current && setExplanation(data))
      .catch((e) => current && setError(explainErrorMessage(e)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [member]);

  useEffect(() => {
    // Picking someone fills the input with their label: that isn't a search
    const isPickedLabel = member !== null && search === memberName(member);
    if (!canExplainOthers || isPickedLabel || search.trim().length < 2) {
      setOptions([]);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      authzService
        .searchMembers(search.trim())
        .then((users) => current && setOptions(users))
        .catch(() => current && setOptions([]));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [search, canExplainOthers, member]);

  const { areas, scoped, other } = useMemo(() => {
    const permissions = explanation?.permissions ?? [];
    return {
      areas: permissions.filter((p) => p.permission.startsWith(MODULE_PREFIX)),
      scoped: permissions.filter((p) => !p.permission.startsWith(MODULE_PREFIX) && p.dimensioned),
      other: permissions.filter((p) => !p.permission.startsWith(MODULE_PREFIX) && !p.dimensioned),
    };
  }, [explanation]);

  const label = (p: ExplainPermission) => labels[p.permission] ?? p.label;
  // Own access whenever the person shown is you, picked or not: every value is named then
  const ownAccess = explanation !== null && explanation.user_id === state.user?.userId;
  const areaLevel = (p: ExplainPermission) => {
    const levels = p.grants.map((g) => g.module_level).filter(Boolean);
    return levels.includes('edit') ? 'edit' : 'read';
  };

  return (
    <Box data-testid="effective-access-panel">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '80ch' }}>
        What a person can open and do in this tenant, and on which records, worked out the same way
        the system decides it. Each line is one grant: roles add together but never blend, so a
        Europe grant from one role and a Product B grant from another don&apos;t combine.
      </Typography>

      <Autocomplete
        sx={{ maxWidth: 480, mb: 3 }}
        disabled={!canExplainOthers}
        options={options}
        value={member}
        onChange={(_, value) => setMember(value)}
        inputValue={search}
        onInputChange={(_, value) => setSearch(value)}
        getOptionLabel={memberName}
        isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
        filterOptions={(x) => x}
        noOptionsText={search.trim().length < 2 ? 'Type a name or email' : 'No members found'}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Effective access as"
            placeholder="You"
            helperText={
              canExplainOthers
                ? 'Leave empty to see your own access'
                : 'Showing your own access. Seeing someone else’s needs user and audit access.'
            }
            inputProps={{ ...params.inputProps, 'data-testid': 'effective-access-member' }}
          />
        )}
      />

      {member?.tenant_id && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 480 }} data-testid="effective-access-tenant">
          Showing access in {member.tenant_name || member.tenant_id}, not the tenant you are signed
          in to.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="effective-access-error">
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress
          size={28}
          aria-label="Loading access"
          data-testid="effective-access-loading"
        />
      ) : (
        explanation && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Roles in this tenant
              </Typography>
              {explanation.roles.length ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {explanation.roles.map((r) => (
                    <Chip
                      key={r.role_id}
                      label={r.name}
                      size="small"
                      data-testid="effective-access-role"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None: only what every member gets.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Areas
              </Typography>
              {areas.length ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {areas.map((a) => (
                    <Chip
                      key={a.permission}
                      size="small"
                      variant="outlined"
                      label={`${label(a)}: ${LEVEL_LABEL[areaLevel(a)]}`}
                      data-testid="effective-access-area"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No areas.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Scoped permissions
              </Typography>
              {scoped.length ? (
                <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" data-testid="effective-access-scoped">
                    <TableHead>
                      <TableRow>
                        <TableCell>Permission</TableCell>
                        <TableCell>Granted by</TableCell>
                        {dimensions.map((d) => (
                          <TableCell key={d.key}>{d.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupByArea(scoped, areaLabels).map(([area, permissions]) => {
                        const open = Boolean(openAreas[area]);
                        return (
                          <React.Fragment key={area}>
                            <TableRow
                              hover
                              sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
                              onClick={() => setOpenAreas((o) => ({ ...o, [area]: !open }))}
                              data-testid={`effective-access-group-${area}`}
                            >
                              <TableCell colSpan={dimensions.length + 2}>
                                <IconButton
                                  size="small"
                                  aria-label={open ? `Collapse ${area}` : `Expand ${area}`}
                                  aria-expanded={open}
                                >
                                  {open ? (
                                    <ExpandMoreIcon fontSize="small" />
                                  ) : (
                                    <ChevronRightIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <Typography component="span" variant="body2" fontWeight={600}>
                                  {area} ({permissions.length})
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {open &&
                              permissions.flatMap((p) =>
                                p.grants.map((g, index) => (
                                  <TableRow key={`${p.permission}-${g.grant_id}`}>
                                    {index === 0 && (
                                      <TableCell rowSpan={p.grants.length}>
                                        <Tooltip title={p.permission}>
                                          <span tabIndex={0}>{label(p)}</span>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                    <TableCell>{grantSource(g)}</TableCell>
                                    {dimensions.map((d) => {
                                      const coverage = g.dimensions[d.key];
                                      const text = describeCoverage(coverage, ownAccess);
                                      return (
                                        <TableCell
                                          key={d.key}
                                          data-testid={`coverage-${p.permission}-${d.key}`}
                                        >
                                          {coverage && !coverage.all && coverage.count ? (
                                            <Tooltip title={`Covers ${coverage.count} value(s)`}>
                                              <span tabIndex={0}>{text}</span>
                                            </Tooltip>
                                          ) : (
                                            text
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                ))
                              )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No scoped permissions.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Other permissions
              </Typography>
              {other.length ? (
                <Stack spacing={1}>
                  {groupByArea(other, areaLabels).map(([area, permissions]) => (
                    <Box key={area} data-testid={`effective-access-other-group-${area}`}>
                      <Typography variant="caption" color="text.secondary">
                        {area}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {permissions.map((p) => (
                          <Tooltip key={p.permission} title={p.permission}>
                            <Chip
                              size="small"
                              label={label(p)}
                              data-testid="effective-access-other"
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              )}
            </Box>
          </Stack>
        )
      )}
    </Box>
  );
};

export default EffectiveAccessPanel;
